import { useState, useMemo, useRef } from "react";
import { Pencil, UserCheck, UserX, UserPlus, Search, XCircle } from "lucide-react";
import { Modal } from "@/shared/components/Modal";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { ModalSairSemSalvar } from "@/shared/components/ModalSairSemSalvar";
import { Button } from "@/shared/components/Button";
import { Paginacao } from "@/shared/components/Paginacao";
import { FormularioUsuario, type FormularioUsuarioValores } from "@/core/components/FormularioUsuario";
import {
  useUsuarios,
  useUsuariosPendentes,
  useCriarUsuarioAdmin,
  useAtualizarUsuario,
  useAprovarUsuario,
  useInativarUsuario,
  useReativarUsuario,
} from "@/core/hooks/useUsuarios";
import { formatarCpf } from "@/core/utils/cpf";
import { rotuloSexo, soSexo } from "@/shared/lib/sexo";
import { useAuth } from "@/core/hooks/useAuth";
import type { Pessoa } from "@/core/services/pessoasService";
import type { PessoaPerfil } from "@/shared/types/database.types";

const ITENS_POR_PAGINA = 10;

type AcaoStatusPendente =
  | { tipo: "aprovar" | "rejeitar" | "inativar" | "reativar"; pessoa: Pessoa }
  | null;

/** Troca de perfil pela etiqueta (Skill v3.0), pendente de confirmação. */
type TrocaPerfilPendente = { pessoa: Pessoa; novoPerfil: PessoaPerfil } | null;

export function GerenciarUsuarios() {
  const { data: usuarios, isLoading } = useUsuarios();
  const { data: pendentes } = useUsuariosPendentes();
  const { pessoa: pessoaLogada } = useAuth();

  const criarUsuario = useCriarUsuarioAdmin();
  const atualizarUsuario = useAtualizarUsuario();
  const aprovarUsuario = useAprovarUsuario();
  const inativarUsuario = useInativarUsuario();
  const reativarUsuario = useReativarUsuario();

  const [modalAberto, setModalAberto] = useState(false);
  const [pessoaEmEdicao, setPessoaEmEdicao] = useState<Pessoa | null>(null);
  const [filtroPendentes, setFiltroPendentes] = useState(false);
  /** Separa Membro Comum de Administrador da Plataforma na listagem. */
  const [filtroPerfil, setFiltroPerfil] = useState<"todos" | PessoaPerfil>("todos");
  // F2-02 (revisão 20/07/2026): faltava filtrar por status (ativo/inativo) —
  // antes só dava pra ver isso rolando a lista inteira. "Pendente" continua
  // tendo o botão de destaque próprio acima, esse filtro é só ativo x inativo.
  const [filtroStatus, setFiltroStatus] = useState<"todos" | "ativo" | "inativo">("todos");
  const [pesquisa, setPesquisa] = useState("");
  const [paginaAtual, setPaginaAtual] = useState(1);
  // Atalho do aviso "N pessoas sem sexo informado" (02/11/2026): mostra só quem ainda não tem.
  const [filtroSemSexo, setFiltroSemSexo] = useState(false);

  const totalSemSexo = useMemo(() => (usuarios ?? []).filter((p) => !rotuloSexo(p.sexo)).length, [usuarios]);
  // Se o administrador completar o último cadastro com o filtro ligado, o aviso some e o filtro
  // não pode ficar "preso" mostrando uma lista vazia.
  const filtrandoSemSexo = filtroSemSexo && totalSemSexo > 0;

  const listaBase = filtroPendentes ? pendentes : usuarios;

  const listaFiltrada = useMemo(() => {
    if (!listaBase) return listaBase;
    const termo = pesquisa.trim().toLowerCase();
    const termoNumerico = termo.replace(/\D/g, "");
    return listaBase.filter((p) => {
      if (filtroPerfil !== "todos" && p.perfil !== filtroPerfil) return false;
      if (filtroStatus !== "todos" && p.status !== filtroStatus) return false;
      if (filtrandoSemSexo && rotuloSexo(p.sexo)) return false;
      if (!termo) return true;
      const bateNome = p.nome_completo.toLowerCase().includes(termo);
      const bateCpf = termoNumerico.length > 0 && !!p.cpf && p.cpf.includes(termoNumerico);
      return bateNome || bateCpf;
    });
  }, [listaBase, pesquisa, filtroPerfil, filtroStatus, filtrandoSemSexo]);

  const chaveFiltros = `${filtroPendentes}-${filtroPerfil}-${filtroStatus}-${filtrandoSemSexo}-${pesquisa}`;
  const [chaveFiltrosAnterior, setChaveFiltrosAnterior] = useState(chaveFiltros);
  if (chaveFiltros !== chaveFiltrosAnterior) {
    setChaveFiltrosAnterior(chaveFiltros);
    setPaginaAtual(1);
  }

  const totalPaginas = Math.max(1, Math.ceil((listaFiltrada?.length ?? 0) / ITENS_POR_PAGINA));
  const listaExibida = useMemo(() => {
    if (!listaFiltrada) return listaFiltrada;
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    return listaFiltrada.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [listaFiltrada, paginaAtual]);

  const [mensagem, setMensagem] = useState<{ tipo: "sucesso" | "erro"; texto: string } | null>(null);
  const [erroFormulario, setErroFormulario] = useState<string | null>(null);
  const [formularioSujo, setFormularioSujo] = useState(false);
  const [confirmandoFechamento, setConfirmandoFechamento] = useState(false);
  const [acaoStatusPendente, setAcaoStatusPendente] = useState<AcaoStatusPendente>(null);
  // Trava síncrona contra duplo clique/toque: o botão só fica desabilitado depois do
  // próximo render, e dois cliques muito rápidos chegariam antes disso.
  const processandoAcaoStatusRef = useRef(false);
  const [trocaPerfilPendente, setTrocaPerfilPendente] = useState<TrocaPerfilPendente>(null);

  function exibirMensagem(tipo: "sucesso" | "erro", texto: string) {
    setMensagem({ tipo, texto });
    setTimeout(() => setMensagem(null), 3500);
  }

  function abrirCriacao() {
    setPessoaEmEdicao(null);
    setErroFormulario(null);
    setFormularioSujo(false);
    setModalAberto(true);
  }

  function abrirEdicao(pessoa: Pessoa) {
    setPessoaEmEdicao(pessoa);
    setErroFormulario(null);
    setFormularioSujo(false);
    setModalAberto(true);
  }

  /**
   * Se o formulário tem alterações não salvas, pede confirmação antes de
   * fechar (evita perder dados digitados por engano — toque acidental fora
   * do modal, ou no X). Se está limpo, fecha direto.
   */
  function tentarFecharModal() {
    if (formularioSujo) {
      setConfirmandoFechamento(true);
    } else {
      fecharModalDeVerdade();
    }
  }

  function fecharModalDeVerdade() {
    setModalAberto(false);
    setErroFormulario(null);
    setFormularioSujo(false);
    setConfirmandoFechamento(false);
  }

  async function handleCriar(dados: FormularioUsuarioValores & { senha: string }) {
    setErroFormulario(null);
    // O formulário já exige o sexo ao criar; a conferência aqui só estreita o tipo.
    const sexo = soSexo(dados.sexo);
    if (!sexo) {
      setErroFormulario("Informe o sexo.");
      return;
    }
    try {
      await criarUsuario.mutateAsync({
        cpf: dados.cpf,
        nomeCompleto: dados.nomeCompleto,
        telefone: dados.telefone,
        email: dados.email,
        dataNascimento: dados.dataNascimento,
        sexo,
        senha: dados.senha,
        perfil: dados.perfil,
      });
      setModalAberto(false);
      setFormularioSujo(false);
      exibirMensagem("sucesso", "Usuário criado com sucesso.");
    } catch (erro) {
      setErroFormulario(erro instanceof Error ? erro.message : "Não foi possível criar o usuário.");
    }
  }

  async function handleEditar(
    dados: Omit<FormularioUsuarioValores, "senha" | "perfil" | "_ehEdicao">,
  ) {
    if (!pessoaEmEdicao) return;
    setErroFormulario(null);
    try {
      // Perfil NÃO é editado aqui — a troca é pela etiqueta na listagem (Skill v3.0).
      await atualizarUsuario.mutateAsync({
        id: pessoaEmEdicao.id,
        dados: {
          nome_completo: dados.nomeCompleto,
          telefone: dados.telefone ?? null,
          email: dados.email,
          data_nascimento: dados.dataNascimento,
          // Em branco = não altera (cadastro antigo salvo sem escolher); nunca apaga o que já existe.
          ...(dados.sexo ? { sexo: dados.sexo } : {}),
        },
      });
      setModalAberto(false);
      setFormularioSujo(false);
      exibirMensagem("sucesso", "Usuário atualizado com sucesso.");
    } catch (erro) {
      setErroFormulario(erro instanceof Error ? erro.message : "Não foi possível atualizar o usuário.");
    }
  }

  async function confirmarAcaoStatus() {
    if (!acaoStatusPendente || processandoAcaoStatusRef.current) return;
    processandoAcaoStatusRef.current = true;
    const { tipo, pessoa } = acaoStatusPendente;
    try {
      if (tipo === "aprovar") {
        await aprovarUsuario.mutateAsync(pessoa.id);
        exibirMensagem("sucesso", "Usuário aprovado com sucesso.");
      } else if (tipo === "rejeitar") {
        // Na tela o verbo é "Recusar" (padrão do sistema); internamente o tipo segue
        // "rejeitar", como em Empréstimos/Eventos/Reservas. O banco não tem um estado
        // "recusado" (pessoas.status é pendente | ativo | inativo): recusar a
        // solicitação reaproveita "inativo". A pessoa não consegue entrar, e um
        // administrador ainda pode reativá-la.
        await inativarUsuario.mutateAsync(pessoa.id);
        exibirMensagem("sucesso", "Solicitação recusada.");
      } else if (tipo === "inativar") {
        await inativarUsuario.mutateAsync(pessoa.id);
        exibirMensagem("sucesso", "Usuário inativado.");
      } else {
        await reativarUsuario.mutateAsync(pessoa.id);
        exibirMensagem("sucesso", "Usuário reativado com sucesso.");
      }
      setAcaoStatusPendente(null);
    } catch {
      setAcaoStatusPendente(null);
      const mensagemPadrao =
        tipo === "aprovar" ? "Não foi possível aprovar o usuário."
        : tipo === "rejeitar" ? "Não foi possível recusar a solicitação."
        : tipo === "inativar" ? "Não foi possível inativar o usuário."
        : "Não foi possível reativar o usuário.";
      exibirMensagem("erro", mensagemPadrao);
    } finally {
      processandoAcaoStatusRef.current = false;
    }
  }

  async function confirmarTrocaPerfil() {
    if (!trocaPerfilPendente) return;
    const { pessoa, novoPerfil } = trocaPerfilPendente;
    try {
      await atualizarUsuario.mutateAsync({ id: pessoa.id, dados: { perfil: novoPerfil } });
      setTrocaPerfilPendente(null);
      exibirMensagem(
        "sucesso",
        novoPerfil === "administrador"
          ? `${pessoa.nome_completo} agora é Administrador da igreja.`
          : `${pessoa.nome_completo} agora é Membro.`,
      );
    } catch {
      setTrocaPerfilPendente(null);
      exibirMensagem("erro", "Não foi possível alterar o perfil.");
    }
  }

  const carregandoAcaoStatus = aprovarUsuario.isPending || inativarUsuario.isPending || reativarUsuario.isPending;

  const textoConfirmacaoStatus: Record<
    NonNullable<AcaoStatusPendente>["tipo"],
    { titulo: string; mensagem: string; texto: string; variante: "perigo" | "padrao" }
  > = {
    aprovar: {
      titulo: "Aprovar usuário",
      mensagem: `Aprovar o cadastro de "${acaoStatusPendente?.pessoa.nome_completo}"? Ele poderá fazer login normalmente depois disso.`,
      texto: "Aprovar",
      variante: "padrao",
    },
    rejeitar: {
      titulo: "Recusar solicitação",
      mensagem: `Recusar a solicitação de entrada de "${acaoStatusPendente?.pessoa.nome_completo}" na igreja? A pessoa não conseguirá entrar até ser reativada.`,
      texto: "Recusar",
      variante: "perigo",
    },
    inativar: {
      titulo: "Inativar usuário",
      mensagem: `Inativar "${acaoStatusPendente?.pessoa.nome_completo}"? Ele não conseguirá mais fazer login até ser reativado.`,
      texto: "Inativar",
      variante: "perigo",
    },
    reativar: {
      titulo: "Reativar usuário",
      mensagem: `Reativar "${acaoStatusPendente?.pessoa.nome_completo}"? Ele voltará a conseguir fazer login normalmente.`,
      texto: "Reativar",
      variante: "padrao",
    },
  };

  const badgeStatus = (status: Pessoa["status"]) => {
    if (status === "ativo") return "bg-success/10 text-success";
    if (status === "inativo") return "bg-surface-2 text-muted";
    return "bg-warning/10 text-warning";
  };

  const labelStatus = (status: Pessoa["status"]) => {
    if (status === "ativo") return "Ativo";
    if (status === "inativo") return "Inativo";
    return "Pendente";
  };

  return (
    <>
      <main className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6 sm:py-10">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-brand-verde">Usuários</h1>
          <Button onClick={abrirCriacao} className="bg-brand-verde text-white">
            + Novo usuário
          </Button>
        </div>

        {!!pendentes?.length && (
          <button
            onClick={() => setFiltroPendentes((v) => !v)}
            className={`mt-4 rounded-md border px-4 py-2 text-sm font-medium transition-colors ${
              filtroPendentes
                ? "border-brand-verde bg-brand-verde text-white"
                : "border-warning bg-warning/10 text-warning"
            }`}
          >
            {pendentes.length} cadastro(s) pendente(s) de aprovação{" "}
            {filtroPendentes ? "— mostrando só pendentes" : "— clique para ver"}
          </button>
        )}

        {/* Aviso para o administrador: cadastros antigos sem sexo. Some sozinho quando todos estiverem
            preenchidos. Ninguém é bloqueado de usar o Elos por causa disso. */}
        {totalSemSexo > 0 && (
          <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-warning/50 bg-warning/10 px-4 py-3 text-sm text-text">
            <span>
              <b>{totalSemSexo === 1 ? "1 pessoa" : `${totalSemSexo} pessoas`}</b> sem sexo informado.
            </span>
            <button
              type="button"
              onClick={() => setFiltroSemSexo((v) => !v)}
              aria-pressed={filtrandoSemSexo}
              className="shrink-0 rounded-md border border-warning/60 px-3 py-1.5 text-xs font-semibold text-warning transition-colors hover:bg-warning/10"
            >
              {filtrandoSemSexo ? "Ver todos" : "Ver quais"}
            </button>
          </div>
        )}

        {mensagem && (
          <div
            role="alert"
            className={`mt-4 rounded-xl px-4 py-3 text-sm font-medium ${
              mensagem.tipo === "sucesso"
                ? "bg-success/10 text-success"
                : "bg-danger/10 text-danger"
            }`}
          >
            {mensagem.texto}
          </div>
        )}

        {/* Separa Membro Comum de Administrador da Plataforma — evita ter que
            procurar "na mão" numa lista única misturada. */}
        <div className="mt-4 flex gap-1.5 overflow-x-auto">
          {(
            [
              { valor: "todos", rotulo: "Todos" },
              { valor: "membro", rotulo: "Membros" },
              { valor: "administrador", rotulo: "Administradores" },
            ] as { valor: "todos" | PessoaPerfil; rotulo: string }[]
          ).map((op) => (
            <button
              key={op.valor}
              type="button"
              onClick={() => setFiltroPerfil(op.valor)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                filtroPerfil === op.valor
                  ? "bg-brand-verde text-white"
                  : "border border-border bg-surface text-muted hover:text-text"
              }`}
            >
              {op.rotulo}
            </button>
          ))}
        </div>

        {/* F2-02: filtro por status (ativo/inativo), separado do filtro de perfil. */}
        <div className="mt-2 flex gap-1.5 overflow-x-auto">
          {(
            [
              { valor: "todos", rotulo: "Qualquer status" },
              { valor: "ativo", rotulo: "Ativos" },
              { valor: "inativo", rotulo: "Inativos" },
            ] as { valor: "todos" | "ativo" | "inativo"; rotulo: string }[]
          ).map((op) => (
            <button
              key={op.valor}
              type="button"
              onClick={() => setFiltroStatus(op.valor)}
              className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                filtroStatus === op.valor
                  ? "bg-surface-2 text-text ring-1 ring-primary"
                  : "border border-border bg-surface text-muted hover:text-text"
              }`}
            >
              {op.rotulo}
            </button>
          ))}
        </div>

        <div className="relative mt-4">
          <Search size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted" />
          <input
            type="search"
            placeholder="Pesquisar por nome ou CPF..."
            value={pesquisa}
            onChange={(e) => setPesquisa(e.target.value)}
            className="w-full rounded-xl border border-border bg-surface py-3 pl-11 pr-4 text-sm text-text placeholder:text-muted focus:border-brand-verde focus:outline-none focus:ring-1 focus:ring-brand-verde"
          />
        </div>

        <div className="mt-4 overflow-hidden rounded-xl border border-border bg-surface">
          {isLoading ? (
            <p className="p-6 text-sm text-muted">Carregando...</p>
          ) : !listaExibida?.length ? (
            <p className="p-6 text-sm text-muted">
              {filtroPendentes ? "Nenhum cadastro pendente." : "Nenhum usuário cadastrado."}
            </p>
          ) : (
            <ul>
              {listaExibida.map((pessoa, idx) => (
                <li
                  key={pessoa.id}
                  className={`flex items-center gap-3 px-4 py-3 ${idx > 0 ? "border-t border-border" : ""}`}
                >
                  {/* Info principal */}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-text">
                      {pessoa.nome_completo}
                    </p>
                    <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-xs text-muted">
                      <span>{pessoa.cpf ? formatarCpf(pessoa.cpf) : "Sem CPF"}</span>
                      {/*
                       * Etiqueta de perfil de PLATAFORMA (DA-002 Nível 1), clicável
                       * (Skill v3.0): alterna Administrador/Membro com confirmação.
                       * Guarda: ninguém rebaixa a si mesmo (evita igreja sem admin).
                       * "Gestor de módulo" é outra coisa — se define no Ministério.
                       */}
                      <button
                        type="button"
                        disabled={pessoa.id === pessoaLogada?.id}
                        title={
                          pessoa.id === pessoaLogada?.id
                            ? "Você não pode alterar o próprio perfil."
                            : "Clique para trocar o perfil da plataforma"
                        }
                        onClick={() =>
                          setTrocaPerfilPendente({
                            pessoa,
                            novoPerfil:
                              pessoa.perfil === "administrador" ? "membro" : "administrador",
                          })
                        }
                        className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                          pessoa.perfil === "administrador"
                            ? "bg-primary/10 text-primary hover:bg-primary/20"
                            : "bg-surface-2 text-muted hover:bg-border"
                        } disabled:cursor-not-allowed disabled:opacity-60`}
                      >
                        {pessoa.perfil === "administrador" ? "Administrador" : "Membro"}
                      </button>
                      <span
                        className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${badgeStatus(pessoa.status)}`}
                      >
                        {labelStatus(pessoa.status)}
                      </span>
                      {/* Selo do sexo: neutro quando informado, em alerta quando falta. */}
                      <span
                        className={`inline-block rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                          rotuloSexo(pessoa.sexo) ? "bg-surface-2 text-muted" : "bg-warning/10 text-warning"
                        }`}
                      >
                        {rotuloSexo(pessoa.sexo) ?? "Sexo não informado"}
                      </span>
                    </p>
                  </div>

                  {/* Ações contextuais por ícone — cores fixas (não dependem de hover, funciona em mobile) */}
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={() => abrirEdicao(pessoa)}
                      aria-label="Editar"
                      className="rounded-lg p-2 text-info transition-colors hover:bg-info/10"
                    >
                      <Pencil size={16} />
                    </button>
                    {pessoa.status === "pendente" && (
                      <>
                        <button
                          onClick={() => setAcaoStatusPendente({ tipo: "aprovar", pessoa })}
                          aria-label="Aprovar"
                          className="rounded-lg p-2 text-success transition-colors hover:bg-success/10"
                        >
                          <UserCheck size={16} />
                        </button>
                        <button
                          onClick={() => setAcaoStatusPendente({ tipo: "rejeitar", pessoa })}
                          aria-label="Recusar"
                          className="rounded-lg p-2 text-danger transition-colors hover:bg-danger/10"
                        >
                          <XCircle size={16} />
                        </button>
                      </>
                    )}
                    {pessoa.status === "ativo" && (
                      <button
                        onClick={() => setAcaoStatusPendente({ tipo: "inativar", pessoa })}
                        aria-label="Inativar"
                        className="rounded-lg p-2 text-danger transition-colors hover:bg-danger/10"
                      >
                        <UserX size={16} />
                      </button>
                    )}
                    {pessoa.status === "inativo" && (
                      <button
                        onClick={() => setAcaoStatusPendente({ tipo: "reativar", pessoa })}
                        aria-label="Reativar"
                        className="rounded-lg p-2 text-success transition-colors hover:bg-success/10"
                      >
                        <UserPlus size={16} />
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <Paginacao
          paginaAtual={paginaAtual}
          totalPaginas={totalPaginas}
          onMudarPagina={setPaginaAtual}
        />
      </main>

      <Modal
        titulo={pessoaEmEdicao ? "Editar usuário" : "Novo usuário"}
        aberto={modalAberto}
        onClose={tentarFecharModal}
        protegerSaida={false}
        carregando={criarUsuario.isPending || atualizarUsuario.isPending}
      >
        <FormularioUsuario
          pessoaEmEdicao={pessoaEmEdicao}
          carregando={criarUsuario.isPending || atualizarUsuario.isPending}
          erro={erroFormulario}
          onDirtyChange={setFormularioSujo}
          onSubmitCriar={handleCriar}
          onSubmitEditar={handleEditar}
        />
      </Modal>

      <ModalSairSemSalvar
        aberto={confirmandoFechamento}
        onSair={fecharModalDeVerdade}
        onContinuar={() => setConfirmandoFechamento(false)}
      />

      {trocaPerfilPendente && (
        <ModalConfirmacao
          aberto={!!trocaPerfilPendente}
          titulo={
            trocaPerfilPendente.novoPerfil === "administrador"
              ? "Tornar administrador"
              : "Tornar membro"
          }
          mensagem={
            trocaPerfilPendente.novoPerfil === "administrador"
              ? `Tornar "${trocaPerfilPendente.pessoa.nome_completo}" Administrador da igreja? A pessoa passa a gerenciar usuários, ministérios, elos e a igreja.`
              : `Tornar "${trocaPerfilPendente.pessoa.nome_completo}" Membro? A pessoa perde o acesso às telas da Administração (usuários, ministérios, elos, igreja). Funções ministeriais não são afetadas.`
          }
          textoBotaoConfirmar={
            trocaPerfilPendente.novoPerfil === "administrador" ? "Tornar administrador" : "Tornar membro"
          }
          variante={trocaPerfilPendente.novoPerfil === "administrador" ? "padrao" : "perigo"}
          carregando={atualizarUsuario.isPending}
          onConfirmar={confirmarTrocaPerfil}
          onCancelar={() => setTrocaPerfilPendente(null)}
        />
      )}

      {acaoStatusPendente && (
        <ModalConfirmacao
          aberto={!!acaoStatusPendente}
          titulo={textoConfirmacaoStatus[acaoStatusPendente.tipo].titulo}
          mensagem={textoConfirmacaoStatus[acaoStatusPendente.tipo].mensagem}
          textoBotaoConfirmar={textoConfirmacaoStatus[acaoStatusPendente.tipo].texto}
          variante={textoConfirmacaoStatus[acaoStatusPendente.tipo].variante}
          carregando={carregandoAcaoStatus}
          onConfirmar={confirmarAcaoStatus}
          onCancelar={() => setAcaoStatusPendente(null)}
        />
      )}
    </>
  );
}
